import { useRef, type CSSProperties, type KeyboardEvent } from "react";
import tokens from "@vladrss/tokens/tokens.json";
import { Icon } from "./Icon";
import { setPrefs, usePrefs } from "../lib/prefs";

/**
 * A radio group's keyboard, as the role promises: the chosen option is the group's one tab stop;
 * the arrow keys move the choice and the focus together, wrapping round; Home and End go to the
 * ends. With nothing chosen the first option is the way in.
 */
function useRadioGroup(count: number, chosen: number, choose: (i: number) => void) {
  const ref = useRef<HTMLDivElement>(null);
  const stop = Math.max(chosen, 0);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.altKey || e.ctrlKey || e.metaKey || !count) return;
    const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    const to = step ? (stop + step + count) % count : e.key === "Home" ? 0 : e.key === "End" ? count - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    choose(to);
    ref.current?.querySelectorAll<HTMLElement>('[role="radio"]')[to]?.focus();
  };
  return { ref, onKeyDown, tabIndex: (i: number) => (i === stop ? 0 : -1) };
}

/** A row of mutually exclusive choices, used wherever a setting has a few named values. */
export function Seg<T extends string | number | null>({ value, options, onChange, label }: { value: T; options: [T, string, string?][]; onChange: (v: T) => void; label: string }) {
  const group = useRadioGroup(options.length, options.findIndex(([v]) => v === value), (i) => onChange(options[i]![0]));
  return (
    <div className="seg" role="radiogroup" aria-label={label} ref={group.ref} onKeyDown={group.onKeyDown}>
      {options.map(([v, text, icon], i) => <button key={String(v)} className={value === v ? "on" : ""} role="radio" aria-checked={value === v} tabIndex={group.tabIndex(i)} onClick={() => onChange(v)}>{icon && <Icon name={icon} size={16} />}{text}</button>)}
    </div>
  );
}

/** Characters per line, in five steps; the ends show a short and a long paragraph, and each stop's dash is as long as its lines. */
export function LineLength() {
  const p = usePrefs();
  const group = useRadioGroup(tokens.type.measure.length, p.measure, (i) => setPrefs({ measure: i }));
  return (
    <div className="sizes"><span className="ll ll1" aria-hidden="true" /><div className="stops long" role="radiogroup" aria-label="Line length" ref={group.ref} onKeyDown={group.onKeyDown}>{tokens.type.measure.map((n, i) => <button key={n} style={{ "--i": i } as CSSProperties} className={p.measure === i ? "on" : ""} role="radio" aria-checked={p.measure === i} tabIndex={group.tabIndex(i)} aria-label={`${n} characters a line`} onClick={() => setPrefs({ measure: i })} />)}</div><span className="ll ll2" aria-hidden="true" /></div>
  );
}

/** Five text-size stops between a small and a large A, each dot as big as its step; shared by Settings and the reader's Aa popover. */
export function TextSize() {
  const p = usePrefs();
  const group = useRadioGroup(tokens.type.readSizes.length, p.size, (i) => setPrefs({ size: i }));
  return (
    <div className="sizes"><span className="a1">A</span><div className="stops grow" role="radiogroup" aria-label="Text size" ref={group.ref} onKeyDown={group.onKeyDown}>{tokens.type.readSizes.map((s, i) => <button key={s} style={{ "--i": i } as CSSProperties} className={p.size === i ? "on" : ""} role="radio" aria-checked={p.size === i} tabIndex={group.tabIndex(i)} aria-label={`${s} px`} onClick={() => setPrefs({ size: i })} />)}</div><span className="a2">A</span></div>
  );
}
